/**
 * Make TanStack Query use OUR connectivity signal, not the browser's flag.
 *
 * WHAT WAS WRONG
 * `connectivity.ts` exists because `navigator.onLine === false` is a notorious
 * false-negative: it flips to "offline" with full signal on captive-portal
 * probes, tower hand-offs, VPN reconnects and OS sleep/wake. Every line of our
 * own code was taught to distrust it — and then the data layer underneath all
 * of it went on believing it anyway. TanStack Query ships its own
 * `onlineManager`, and out of the box it flips on nothing but the `window`
 * `online`/`offline` events.
 *
 * That matters because of what the default `networkMode: "online"` does with
 * that opinion. While the manager says offline, Query does not run queries OR
 * mutations — they sit `paused`. Measured against the real library, not
 * inferred:
 *
 *   • QUERIES — the fetch is never issued, so the service worker (which has
 *     the last-known response sitting in `gwc-api`, ready to serve) is never
 *     asked. A tech in a dead zone got a loading state where cached data was
 *     available, and a tech whose phone merely LIED about being offline got the
 *     same for no reason at all.
 *
 *   • MUTATIONS — this is the serious one. Every field save queues itself from
 *     INSIDE its `mutationFn` (`saveThroughOutbox` / `queueOfflineAction`), so
 *     a paused mutation never reaches the code that would have put the work in
 *     the outbox. The save sat in memory instead of in IndexedDB: fine if the
 *     tech kept the app open until signal returned, gone if he didn't. The
 *     offline queue was being bypassed in precisely the dead zone it was built
 *     for.
 *
 * THE FIX, IN TWO HALVES
 * 1. Point `onlineManager` at `getVerifiedOnline()` — the reachability-probed
 *    state — so Query and the rest of the app finally agree about what
 *    "offline" means.
 * 2. `networkMode: "offlineFirst"` (see `main.tsx`) so a request is ATTEMPTED
 *    once regardless: the service worker gets to answer queries from cache, and
 *    a mutation reaches its own queueing code. Only the retries pause.
 *
 * Imported for its side effect by `main.tsx`, before the QueryClient is built.
 */
import { onlineManager } from "@tanstack/react-query";
import { getVerifiedOnline, subscribeConnectivity } from "./connectivity";

export function wireQueryConnectivity(): void {
    onlineManager.setEventListener((setOnline) => {
        setOnline(getVerifiedOnline());
        return subscribeConnectivity(() => setOnline(getVerifiedOnline()));
    });
}

wireQueryConnectivity();
